import React from 'react';
import {scroller} from 'react-scroll';
import {CONTACT_US} from '../data/Localization';
import PropTypes from 'prop-types';

/**
 * Renders a contact button component.
 * @param {Object} props - The component props.
 * @param {string} props.url - The URL to open when the button is clicked.
 * @param {string} props.label - The label text for the button.
 * @param {string} props.className - The CSS class name for the component.
 * @returns {JSX.Element} The rendered contact button component.
 */
const ContactButton = (props) => {
  const [anchor, setAnchor] = React.useState(null);
  const [fallbackUrl, setFallbackUrl] = React.useState('https://www.esri.com/en-us/contact');

  React.useEffect(() => {
    if (props.url && /^#/.test(props.url)) {
      setAnchor(props.url.substring(1));
    }
  }, [props.url]);

  React.useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname.includes('dev')) {
      setFallbackUrl('https://aem-dev.esri.com/en-us/contact');
    } else if (hostname.includes('uat')) {
      setFallbackUrl('https://uat.esri.com/en-us/contact');
    }
  }, []);

  /**
   * Scrolls to the specified element on the page.
   * @param {string} to - The ID of the element to scroll to.
   * @param {Object} options - Additional options for scrolling.
   */
  const handleScrollTo = (to, options) => {
    scroller.scrollTo(to, {
      smooth: true,
      duration: 500,
      ...options,
    });
  };

  /**
   * Handles the button click event.
   * If an anchor is specified, scrolls to the anchor.
   * Otherwise, opens the URL in a new tab.
   * @param {string} url - The URL to open.
   */
  const handleButtonClick = (url) => {
    if (anchor) {
      handleScrollTo(anchor);
    } else {
      window.open(url, '_blank');
    }
  };

  const url = props.url || fallbackUrl;
  const label = props.label || CONTACT_US;

  return (
    <calcite-button onClick={() => handleButtonClick(url)} scale="l" class="prc__add-to-cart">
      <span>{label}</span>
    </calcite-button>
  );
};
ContactButton.propTypes = {
  url: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
};

/**
 * Renders a purchase options button component.
 * @returns {JSX.Element} The rendered purchase options button component.
 */
const PurchaseOptionsButton = () => {
  const modalRef = React.useRef(null);
  const [label, setLabel] = React.useState(null);
  React.useEffect(() => {
    const pageModal = document.querySelector('#page-modal');
    modalRef.current = pageModal;
    setLabel(modalRef.current?.dataset?.geoblockingModalOpenCtaLabel);
  }, []);

  const openModal = () => {
    modalRef.current.open = true;
  };

  return (
    <calcite-button scale="l" class="prc__add-to-cart" onClick={openModal}>
      {label}
    </calcite-button>
  );
};

/**
 * Renders a wrapper component for the unavailable item button.
 * @param {Object} props - The component props.
 * @param {string} props.url - The link for the contact button.
 * @param {string} props.className - The CSS class name for the component.
 * @returns {JSX.Element} The rendered wrapper component.
 */
const UnavailableItemButton = (props) => {
  const [isPurchaseOptionsVisible, setIsPurchaseOptionsVisible] = React.useState(false);

  React.useEffect(() => {
    if (document.querySelector('#page-modal')?.dataset?.geoblockingModalEnabled) {
      setIsPurchaseOptionsVisible(true);
    }
  }, []);

  return (
    <div className={`prc__show-no-pricing ${props.className}`}>
      {isPurchaseOptionsVisible ? <PurchaseOptionsButton /> : <ContactButton url={props.url} label={props.label} />}
    </div>
  );
};
UnavailableItemButton.propTypes = {
  url: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
};

export default UnavailableItemButton;
